import { useCallback, useEffect, useRef, useState } from "react";
import { Activity } from "lucide-react";
import bg from "../../assets/bg.svg";
import slide1 from "../../assets/assessment-hero/report-slide-1.png";
import slide2 from "../../assets/assessment-hero/report-slide-2.png";
import slide3 from "../../assets/assessment-hero/report-slide-3.png";
import slide4 from "../../assets/assessment-hero/report-slide-4.png";
import slide5 from "../../assets/assessment-hero/report-slide-5.png";
import "./AssessmentHero.css";

const SLIDES = [
  {
    src: slide1,
    alt: "Actual report excerpt: Executive signal snapshot showing AI readiness at a glance",
    caption: "Understand your AI readiness",
    tab: "AI readiness",
  },
  {
    src: slide3,
    alt: "Actual report excerpt: Top AI opportunities prioritized by expected return",
    caption: "Find your top AI opportunities",
    tab: "Top AI opportunities",
  },
  {
    src: slide2,
    alt: "Actual report excerpt: AI maturity position relative to peers, current position and next maturity unlock",
    caption: "See where you stand among peers",
    tab: "AI maturity position relative to peers",
  },
  {
    src: slide4,
    alt: "Actual report excerpt: Opportunity prioritization matrix with recommended first pilot",
    caption: "See what deserves priority",
    tab: "Opportunity prioritization",
  },
  {
    src: slide5,
    alt: "Actual report excerpt: 90-day execution plan",
    caption: "Plan your first 90 days",
    tab: "90-day plan",
  },
] as const;

type Props = {
  onStart: () => void;
};

export const AssessmentHero = ({ onStart }: Props) => {
  const rootRef = useRef<HTMLElement | null>(null);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false,
  );
  const [hover, setHover] = useState(false);
  const touchX = useRef(0);

  const show = useCallback((n: number) => {
    setIndex((n + SLIDES.length) % SLIDES.length);
  }, []);

  useEffect(() => {
    if (paused || hover || document.hidden) return undefined;
    const timer = window.setInterval(() => show(index + 1), 6500);
    return () => window.clearInterval(timer);
  }, [paused, hover, index, show]);

  useEffect(() => {
    const onVisibility = () => {
      // restart effect via state dependency on document.hidden indirectly
      setHover((h) => h);
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  const manual = (n: number) => {
    setPaused(true);
    show(n);
  };

  return (
    <section className="hero">
      <div className="hero-brand flex items-center gap-2.5">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-dark">
          <Activity className="h-4 w-4 text-signal" strokeWidth={2.5} />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-base font-semibold tracking-tight text-foreground">Signal AI</span>
          <span className="inline-flex items-center gap-0">
            <span className="text-xs font-medium text-muted-foreground">by</span>
            <img src={bg} alt="Gowitek" className="h-6 w-auto object-contain" />
          </span>
        </div>
      </div>

      <div className="grid">
        <div>
          <div className="eyebrow">FOR MANUFACTURING LEADERS</div>
          <h1>
            Which AI opportunity
            <br />
            deserves your
            <br />
            <span>capital first?</span>
          </h1>
          <p className="sub">
            <strong>In 9 minutes, see your AI opportunities more clearly.</strong> Get
            insights into readiness, potential returns, and what to prioritize, with a
            practical 90-day plan.
          </p>

          <div className="cta-row">
            <button type="button" className="button" onClick={onStart}>
              Start Your AI ROI Assessment <span>→</span>
            </button>
            <a
              className="sample-link"
              href="/sample-report.pdf"
              download="Signal AI Sample Report.pdf"
            >
              Download Sample Report
            </a>
          </div>

          <div className="price">
            Start at <s>$99</s> <strong>$9</strong>
          </div>
        </div>

        <section
          ref={rootRef}
          className="preview"
          aria-label="Inside the Signal AI report"
          aria-roledescription="carousel"
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          onFocusCapture={() => setHover(true)}
          onBlurCapture={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
              setHover(false);
            }
          }}
        >
          <div
            className="tablet-scene laptop-device"
            aria-label="Laptop showing the Signal AI report"
          >
            <div className="laptop-camera" aria-hidden="true" />
            <div
              className="viewport"
              onTouchStart={(e) => {
                touchX.current = e.changedTouches[0].clientX;
              }}
              onTouchEnd={(e) => {
                const d = e.changedTouches[0].clientX - touchX.current;
                if (Math.abs(d) > 45) manual(index + (d < 0 ? 1 : -1));
              }}
            >
              <div
                className="track"
                style={{ ["--report-offset" as string]: `${-index * 100}%` }}
              >
                {SLIDES.map((slide, i) => (
                  <figure
                    key={slide.tab}
                    className="slide"
                    role="tabpanel"
                    id={`slide-${i}`}
                    aria-labelledby={`tab-${i}`}
                    aria-hidden={i !== index}
                  >
                    <img src={slide.src} alt={slide.alt} />
                    <figcaption>{slide.caption}</figcaption>
                  </figure>
                ))}
              </div>
            </div>
          </div>

          <div className="controls">
            <span className="counter" aria-live="polite">
              {index + 1} / {SLIDES.length}
            </span>
            <button
              type="button"
              className="pause"
              aria-label={paused ? "Play report animation" : "Pause report animation"}
              onClick={() => setPaused((p) => !p)}
            >
              {paused ? "▶" : "Ⅱ"}
            </button>
          </div>

          <div className="tabs" role="tablist" aria-label="Report sections">
            {SLIDES.map((slide, i) => (
              <button
                key={slide.tab}
                type="button"
                role="tab"
                id={`tab-${i}`}
                aria-controls={`slide-${i}`}
                aria-selected={i === index}
                tabIndex={i === index ? 0 : -1}
                onClick={() => manual(i)}
                onKeyDown={(e) => {
                  if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
                    e.preventDefault();
                    const n = (i + (e.key === "ArrowRight" ? 1 : SLIDES.length - 1)) % SLIDES.length;
                    manual(n);
                    document.getElementById(`tab-${n}`)?.focus();
                  }
                }}
              >
                <span className="dot-label">{slide.tab}</span>
              </button>
            ))}
          </div>

          <p className="caption">Actual report excerpts · Results vary by assessment</p>
        </section>
      </div>

      <div className="outputs">
        <div>
          <strong>5</strong> readiness pillars
        </div>
        <div>
          <strong>3</strong> prioritized opportunities
        </div>
        <div>
          <strong>1</strong> recommended first pilot
        </div>
      </div>
    </section>
  );
};

export default AssessmentHero;
